'use client';

import { useRef, useState } from 'react';
import { IonButton, IonButtons, IonIcon, IonModal, IonPicker, IonPickerColumn, IonPickerColumnOption, IonTitle, IonToolbar } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { chevronDown } from 'ionicons/icons';

export type TeeColor = {
  slug: string;
  color: string;
};

export type TeeSelectProps = {
  defaultValue?: string;
  onChange?: (value: string) => void;
  className?: string;
  placeholder?: string;
};

const teeOptions: TeeColor[] = [
  { slug: 'black', color: '#000000' },
  { slug: 'white', color: '#FFFFFF' },
  { slug: 'gold', color: '#FFD700' },
  { slug: 'blue', color: '#0000FF' },
  { slug: 'red', color: '#FF0000' },
  { slug: 'purple', color: '#800080' },
  { slug: 'orange', color: '#FFA500' },
];

function TeeSelect({
  defaultValue,
  onChange = () => { },
  className = '',
  placeholder = 'Select Tee Color',
}: TeeSelectProps) {
  const [selectedTee, setSelectedTee] = useState<string>(defaultValue || '');
  const [showModal, setShowModal] = useState(false);
  const modal = useRef<HTMLIonModalElement>(null);
  const { t } = useTranslation();

  const handleSelect = (teeSlug: string) => {
    setSelectedTee(teeSlug);
    onChange(teeSlug);
    setShowModal(false);
  };

  const selectedTeeOption = teeOptions.find(({ slug }) => slug === selectedTee);

  return (
    <>
      <IonButton
        fill='clear'
        color='transparent'
        onClick={() => setShowModal(true)}
        className={`w-full h-12 max-w-xs text-foreground border-input border rounded-md ${className}`}
      >
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-4">
            {selectedTeeOption ? (
              <>
                <span
                  className="size-6 rounded-full border border-muted-foreground/50"
                  style={{
                    backgroundColor: selectedTeeOption.color,
                  }}
                />
                <span>{t(selectedTeeOption.slug as any)}</span>
              </>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </div>
          <IonIcon icon={chevronDown} className="size-5" />
        </div>
      </IonButton>

      <IonModal
        ref={modal}
        isOpen={showModal}
        onDidDismiss={() => setShowModal(false)}
        className="auto-height"
      >
        <IonToolbar className="border-b">
          <IonButtons slot="start">
            <IonButton onClick={() => setShowModal(false)}>
              {t('cancel')}
            </IonButton>
          </IonButtons>
          <IonButtons slot="end">
            <IonButton onClick={() => {
              if (selectedTee) {
                handleSelect(selectedTee);
              }
            }}>
              {t('done')}
            </IonButton>
          </IonButtons>
        </IonToolbar>

        <div className="picker-wrapper flex-1 overflow-hidden flex items-center justify-center">
          <IonPicker className="w-full h-full">
            <IonPickerColumn value={selectedTee} onIonChange={({ detail }) => {
              if (detail.value !== undefined) {
                setSelectedTee(String(detail.value));
                handleSelect(String(detail.value));
              }
            }}>
              <IonPickerColumnOption value="" disabled={true}>
                --
              </IonPickerColumnOption>
              {teeOptions.map(({ slug, color }) => (
                <IonPickerColumnOption key={slug} value={slug}>
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-block size-4 rounded-full"
                      style={{ backgroundColor: color }}
                    ></span>
                    <span>{t(slug as any)}</span>
                  </div>
                </IonPickerColumnOption>
              ))}
            </IonPickerColumn>
          </IonPicker>
        </div>
      </IonModal>
    </>
  );
}

export default TeeSelect;